import React from 'react';

const FinanceStatCard = ({
  title,
  value,
  Icon,
  tone = 'neutral',
  loading = false,
  featured = false,
  context,
  active = false,
  onClick,
  interactiveOnDesktop = true,
  featuredSpanEnabled = true,
  featuredSpanClass = 'sm:col-span-2 xl:col-span-1',
}) => {
  const isInteractive = typeof onClick === 'function' && !loading;
  const isMobileOnlyInteractive = isInteractive && !interactiveOnDesktop;
  const featuredSpanClasses = featured && featuredSpanEnabled ? featuredSpanClass : '';
  const cardClasses = `${featuredSpanClasses} rounded-[0.85rem] border transition-transform duration-[var(--cp-motion-fast,180ms)] motion-reduce:transition-none ${
    featured
      ? 'border-[color:var(--status-warning-border,var(--cp-border-strong,var(--border)))] bg-[linear-gradient(140deg,color-mix(in_srgb,var(--status-warning-bg,var(--surface-muted))_82%,transparent),var(--cp-surface-soft,var(--surface-muted)))] [box-shadow:0_0_0_1px_color-mix(in_srgb,var(--accent)_20%,transparent),var(--cp-shadow-soft,none)]'
      : 'border-[color:var(--cp-border,var(--border))] bg-[color:var(--cp-surface-soft,var(--surface-muted))] [box-shadow:var(--cp-shadow-glow,none)]'
  } ${active ? 'ring-2 ring-[color:var(--accent)]/35 border-[color:var(--cp-border-strong,var(--border))]' : ''}`;

  const content = (showActionHint = false) => (
    <div className={`${featured ? 'p-3.5 md:p-4' : 'p-3 md:p-3.5'} ${isInteractive ? 'cursor-pointer' : ''}`}>
      <div className={`flex items-start gap-3 ${featured ? 'min-h-[6.4rem] md:min-h-[6.8rem]' : 'min-h-[4.7rem]'}`}>
        <div className="flex-1 min-w-0">
          <p className="text-[11px] md:text-sm font-medium text-[color:var(--text-muted)] leading-tight line-clamp-2">{title}</p>
          {loading ? (
            <>
              <span className={`mt-2 block animate-pulse rounded bg-[color:var(--cp-surface,var(--surface))] ${featured ? 'h-8 w-40' : 'h-6 w-32'}`} />
              {featured && <span className="mt-2 block h-3 w-52 animate-pulse rounded bg-[color:var(--cp-surface,var(--surface))]" />}
            </>
          ) : (
            <>
              <p className={`mt-1 font-bold text-[color:var(--text-primary)] leading-tight tracking-tight tabular-nums ${featured ? 'text-[1.75rem] sm:text-[1.95rem] md:text-[2.05rem]' : 'text-[1.15rem] sm:text-[1.25rem] lg:text-[1.35rem]'}`}>
                {value}
              </p>
              {featured && context && (
                <p className="mt-1 text-xs text-[color:var(--text-muted)] leading-snug">{context}</p>
              )}
            </>
          )}
        </div>
        <span
          className={`flex shrink-0 items-center justify-center rounded-full border ${featured ? 'h-10 w-10 md:h-11 md:w-11' : 'h-8 w-8 md:h-9 md:w-9'}`}
          style={{
            color: `var(--status-${tone}-fg)`,
            backgroundColor: `var(--status-${tone}-bg)`,
            borderColor: `var(--status-${tone}-border)`,
          }}
          aria-hidden="true"
        >
          <Icon className="h-4 w-4 md:h-5 md:w-5" />
        </span>
      </div>
      {showActionHint && isInteractive && !loading && (
        <div className="mt-2 flex items-center justify-between border-t border-[color:var(--cp-border,var(--border))] pt-2">
          <span className="text-[11px] font-medium uppercase tracking-[0.08em] text-[color:var(--text-muted)]">
            Toque para filtrar
          </span>
          <span className="text-[color:var(--text-muted)]" aria-hidden="true">
            &gt;
          </span>
        </div>
      )}
    </div>
  );

  if (isMobileOnlyInteractive) {
    return (
      <li className={cardClasses}>
        <button
          type="button"
          onClick={onClick}
          aria-label={`Filtrar por ${title}`}
          aria-pressed={active}
          className="w-full text-left rounded-[0.85rem] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--cp-surface,var(--surface))] sm:hidden"
        >
          {content(true)}
        </button>
        <div className="hidden sm:block">
          {content(false)}
        </div>
      </li>
    );
  }

  return (
    <li className={cardClasses}>
      {isInteractive ? (
        <button
          type="button"
          onClick={onClick}
          aria-label={`Filtrar por ${title}`}
          aria-pressed={active}
          className="w-full text-left rounded-[0.85rem] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--cp-surface,var(--surface))]"
        >
          {content(true)}
        </button>
      ) : (
        content(false)
      )}
    </li>
  );
};

const FinanceStatCards = ({
  cards = [],
  loading = false,
  ariaLabel = 'Resumo financeiro',
  interactiveOnDesktop = true,
  layout = 'default',
}) => {
  const featuredCards = cards.filter((card) => card.featured);
  const regularCards = cards.filter((card) => !card.featured);
  const orderedCards = [...featuredCards, ...regularCards];
  const isBalancedFiveDesktop = layout === 'balanced-five-desktop';
  const gridClasses = isBalancedFiveDesktop
    ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3 md:gap-4'
    : 'grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 md:gap-4';

  return (
    <section aria-label={ariaLabel}>
      <ul className={gridClasses}>
        {orderedCards.map((card) => (
          <FinanceStatCard
            key={card.id || card.title}
            title={card.title}
            value={card.value}
            Icon={card.Icon}
            tone={card.tone}
            loading={loading}
            featured={card.featured}
            context={card.context}
            active={card.active}
            onClick={card.onClick}
            interactiveOnDesktop={interactiveOnDesktop}
            featuredSpanEnabled={true}
            featuredSpanClass={isBalancedFiveDesktop ? 'xl:col-span-2' : 'sm:col-span-2 xl:col-span-1'}
          />
        ))}
      </ul>
    </section>
  );
};

export default FinanceStatCards;
